import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, FileText } from "lucide-react";
import { CASE_STUDIES } from "../../lib/data";
import { trackEvent, EVENTS } from "../../lib/analytics";

/**
 * RelatedCasesAndCTA — rendered inside each expanded service offering.
 * Reads `offering.caseStudyIds` (co-located on the offering) and renders:
 *   - Related case study links (or a fallback "Browse all case studies")
 *   - A "Contact a practice lead" CTA that deep-links to /contact with
 *     subject + practice prefilled.
 */
export default function RelatedCasesAndCTA({ offering, category }) {
    const caseIds = offering.caseStudyIds || [];
    const cases = caseIds
        .map((id) => CASE_STUDIES.find((c) => c.id === id))
        .filter(Boolean);

    const subject = `${category.name} — ${offering.title}`;
    const contactHref = `/contact?subject=${encodeURIComponent(
        subject
    )}&practice=${encodeURIComponent(category.name)}`;

    return (
        <div
            className="mt-8 pt-7 border-t border-white/5 flex flex-col md:flex-row md:items-end md:justify-between gap-6"
            data-testid={`offering-footer-${offering.id}`}
        >
            <div className="min-w-0 flex-1">
                {cases.length > 0 ? (
                    <>
                        <div className="text-[11px] uppercase tracking-[0.22em] text-brand-green mb-3">
                            Related case studies
                        </div>
                        <div className="flex flex-col gap-2">
                            {cases.map((cs) => (
                                <Link
                                    key={cs.id}
                                    to={`/case-studies#${cs.id}`}
                                    data-testid={`offering-case-${offering.id}-${cs.id}`}
                                    onClick={() =>
                                        trackEvent(EVENTS.CASE_STUDY_CLICKED, {
                                            case_id: cs.id,
                                            from_offering: offering.id,
                                            from_category: category.id,
                                        })
                                    }
                                    className="group inline-flex items-center gap-2 text-[13.5px] text-zinc-300 hover:text-white transition-colors"
                                >
                                    <FileText className="h-3.5 w-3.5 text-brand-blue shrink-0" />
                                    <span className="font-mono text-[10.5px] text-zinc-500 shrink-0">
                                        {cs.industry}
                                    </span>
                                    <span className="truncate">{cs.title}</span>
                                    <ArrowUpRight className="h-3.5 w-3.5 text-zinc-600 group-hover:text-brand-green group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all shrink-0" />
                                </Link>
                            ))}
                        </div>
                    </>
                ) : (
                    <Link
                        to="/case-studies"
                        className="inline-flex items-center gap-2 text-[13px] text-zinc-400 hover:text-white transition"
                        data-testid={`offering-all-cases-${offering.id}`}
                    >
                        <FileText className="h-3.5 w-3.5 text-brand-blue" />
                        Browse all case studies
                        <ArrowUpRight className="h-3.5 w-3.5" />
                    </Link>
                )}
            </div>

            <Link
                to={contactHref}
                data-testid={`offering-contact-lead-${offering.id}`}
                onClick={() =>
                    trackEvent(EVENTS.PRACTICE_LEAD_CTA_CLICKED, {
                        offering_id: offering.id,
                        category_id: category.id,
                    })
                }
                className="btn-glow group relative inline-flex items-center gap-2 rounded-full bg-brand-linear px-5 py-3 text-[13.5px] font-medium text-white hover:shadow-glow-brand transition-all shrink-0 self-start md:self-auto"
            >
                Contact a practice lead
                <ArrowUpRight className="h-4 w-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
            </Link>
        </div>
    );
}
